import { Box, Fade, Skeleton, Stack, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

interface ChartItem {
  month: string;
  value: number;
}

interface Props {
  data: ChartItem[];
  maxValue?: number;
  loading?: boolean;
}

interface MonthMeta {
  xValue: string;
  axisLabel: string;
  tooltipLabel: string;
  rangeLabel: string;
  monthSerial: number | null;
}

const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const YYYY_MM_REGEX = /^(\d{4})-(\d{2})$/;

const MAX_MONTHS_TO_DISPLAY = 12;

const parseYearMonth = (value: string) => {
  const match = YYYY_MM_REGEX.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (!Number.isFinite(year) || monthIndex < 0 || monthIndex > 11) {
    return null;
  }

  return {
    year,
    monthIndex,
    monthSerial: year * 12 + monthIndex,
    key: `${match[1]}-${match[2]}`,
  };
};

const toMonthMeta = (month: string, index: number): MonthMeta => {
  const cleanedMonth = month.trim();
  const parsed = parseYearMonth(cleanedMonth);

  if (!parsed) {
    return {
      xValue: `${cleanedMonth}-${index}`,
      axisLabel: cleanedMonth,
      tooltipLabel: cleanedMonth,
      rangeLabel: cleanedMonth,
      monthSerial: null,
    };
  }

  const { year, monthIndex, monthSerial, key } = parsed;
  const monthName = MONTH_FULL[monthIndex];
  const monthShort = MONTH_SHORT[monthIndex];

  return {
    xValue: key,
    axisLabel: monthShort,
    tooltipLabel: `${monthName} - ${year}`,
    rangeLabel: `${monthShort.toLowerCase()}/${year}`,
    monthSerial,
  };
};

export const AgingConcernLineChart: FC<Props> = ({
  data,
  maxValue,
  loading = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerReady, setContainerReady] = useState(false);
  const [animatedData, setAnimatedData] = useState<number[]>([]);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const chartPoints = useMemo(() => {
    const safeData = Array.isArray(data) ? data : [];

    const mappedPoints = safeData.map((item, index) => {
      const monthMeta = toMonthMeta(item.month, index);
      return {
        ...monthMeta,
        value: Number.isFinite(item.value) ? item.value : 0,
      };
    });

    const hasParsableMonths = mappedPoints.some((point) => point.monthSerial !== null);
    if (!hasParsableMonths) return mappedPoints;

    const orderedPoints = [...mappedPoints].sort((a, b) => {
      if (a.monthSerial === null && b.monthSerial === null) return 0;
      if (a.monthSerial === null) return 1;
      if (b.monthSerial === null) return -1;
      return a.monthSerial - b.monthSerial;
    });

    const firstPointWithMonth = orderedPoints.find((point) => point.monthSerial !== null);
    if (!firstPointWithMonth || firstPointWithMonth.monthSerial === null) {
      return orderedPoints;
    }

    const maxMonthSerial = firstPointWithMonth.monthSerial + (MAX_MONTHS_TO_DISPLAY - 1);
    return orderedPoints.filter(
      (point) => point.monthSerial === null || point.monthSerial <= maxMonthSerial,
    );
  }, [data]);

  const xAxisData = useMemo(
    () => chartPoints.map((point) => point.xValue),
    [chartPoints],
  );

  const values = useMemo(() => {
    return chartPoints.map((point) => point.value);
  }, [chartPoints]);

  const monthLabelsByKey = useMemo(() => {
    const map = new Map<string, { axisLabel: string; tooltipLabel: string; rangeLabel: string }>();
    chartPoints.forEach((point) => {
      map.set(point.xValue, {
        axisLabel: point.axisLabel,
        tooltipLabel: point.tooltipLabel,
        rangeLabel: point.rangeLabel,
      });
    });
    return map;
  }, [chartPoints]);

  // const dateRangeLabel = useMemo(() => {
  //   if (!chartPoints.length) return null;

  //   const first = chartPoints[0].rangeLabel;
  //   const last = chartPoints[chartPoints.length - 1].rangeLabel;
  //   return `${first} - ${last}`;
  // }, [chartPoints]);

  const hasData = useMemo(() => values.some((v) => v > 0), [values]);

  const minVal = useMemo(() => {
    return values.length ? Math.min(...values) : 0;
  }, [values]);

  // Lower bound: least value in response minus 5 (clamped at 0)
  const yMin = useMemo(() => {
    const base = hasData ? minVal - 5 : 0;
    return Math.max(0, base);
  }, [minVal, hasData]);

  // Upper bound: highest value + 10 (or provided maxValue if larger)
  const yMax = useMemo(() => {
    const maxVal = values.length ? Math.max(...values) : 0;
    const computed = maxVal + 10;
    return typeof maxValue === 'number' ? Math.max(computed, maxValue) : computed;
  }, [values, maxValue]);

  // Ensure sensible range and choose 4-6 ticks based on span
  const finalYMax = useMemo(() => (yMax <= yMin ? yMin + 10 : yMax), [yMax, yMin]);
  const tickCount = useMemo(() => {
    const range = finalYMax - yMin;
    if (range <= 15) return 4;
    if (range >= 45) return 6;
    return 5;
  }, [finalYMax, yMin]);

  // Show container and animate line
  useEffect(() => {
    if (!loading) {
      const containerTimer = setTimeout(() => setContainerReady(true), 200);
      const dataTimer = setTimeout(() => setAnimatedData(values), 500);
      return () => {
        clearTimeout(containerTimer);
        clearTimeout(dataTimer);
      };
    } else {
      setContainerReady(false);
      setAnimatedData([]);
    }
  }, [loading, values]);

  // ResizeObserver to track container size
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => {
      setContainerSize({
        width: containerRef.current!.offsetWidth,
        height: containerRef.current!.offsetHeight,
      });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: '#fff',
        borderRadius: 2,
        boxShadow: 2,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
      }}
    >
      <Fade in={containerReady} timeout={400}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" fontWeight={500}>
            Aging Concern
          </Typography>

          <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
            No. of Tickets (exceeds 30 days)
          </Typography>
        </Stack>
      </Fade>

      <Box
        ref={containerRef}
        sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}
      >
        {loading ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Skeleton variant="rectangular" height="85%" sx={{ borderRadius: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="text" width={25} height={16} />
              ))}
            </Box>
          </Box>
        ) : !hasData ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: '#6B7280', textAlign: 'center', mb: 1 }}
            >
              No data available
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#9CA3AF', textAlign: 'center' }}
            >
              No records found
            </Typography>
          </Box>
        ) : (
          <Fade in={containerReady} timeout={600}>
            <Box sx={{ width: '100%', height: '100%' }}>
              {/* Use key based on container size to force re-render on resize */}
              <LineChart
                key={`${containerSize.width}-${containerSize.height}`}
                xAxis={[
                  {
                    scaleType: 'point',
                    data: xAxisData,
                    valueFormatter: (value, context) => {
                      const labels = monthLabelsByKey.get(String(value));
                      if (!labels) return String(value);
                      return context.location === 'tooltip'
                        ? labels.tooltipLabel
                        : labels.axisLabel;
                    },
                    tickLabelStyle: {
                      fill: '#6B7280',
                      fontSize: 12,
                      fontWeight: 500,
                      stroke: 'transparent',
                    },
                  },
                ]}
                yAxis={[
                  {
                    min: yMin,
                    max: finalYMax,
                    tickNumber: tickCount,
                    tickLabelStyle: {
                      fill: '#6B7280',
                      fontSize: 12,
                      fontWeight: 500,
                      stroke: 'transparent',
                    },
                  },
                ]}
                grid={{ horizontal: true }}
                series={[
                  {
                    data: animatedData,
                    curve: 'monotoneX',
                    color: '#088228ff',
                    showMark: false,
                  },
                ]}
                skipAnimation={true}
                sx={{
                  width: '100%',
                  height: '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  '& .MuiChartsAxis-line': { display: 'none' },
                  '& .MuiChartsAxis-domain': { display: 'none' },
                  '& .MuiChartsAxis-tick, & .MuiChartsAxis-tickLine': {
                    stroke: 'transparent',
                  },
                  '& .MuiChartsGrid-line': {
                    strokeDasharray: '6 4',
                    stroke: '#E5E7EB',
                  },
                  '& .MuiLineElement-root': {
                    strokeDasharray: '3000',
                    strokeDashoffset: '3000',
                    animation: animatedData.length
                      ? 'draw-line 2s forwards ease-in'
                      : 'none',
                  },
                  '@keyframes draw-line': {
                    to: { strokeDashoffset: 0 },
                  },
                }}
              />
            </Box>
          </Fade>
        )}
      </Box>

      {/* {!loading && hasData && dateRangeLabel && (
        <Typography
          variant="caption"
          sx={{
            color: '#867a7a',
            textAlign: 'center',
            mt: 1,
            alignSelf: 'center',
            fontSize: '0.8rem',
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {dateRangeLabel}
        </Typography>
      )} */}
    </Box>
  );
};
